import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import InstallButton from '../manager/downloadbutton';

function LoginForm() {
  const [form, setForm] = useState({ email: '', password: '', companyCode: '' });
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [needsReset, setNeedsReset] = useState(false);

  const user = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.loggedOut) {
      navigate('/', { replace: true });
      return;
    }
    if (user) {
      const access = String(user.access || '').trim().toLowerCase();
      const pending = user.staffOnboardPending === true || user.staffOnboardPending === 1;

      if (user.mfaRequired) {
        navigate('/mfa');
        window.location.reload();
      } else if ((access === 'staff' || access === 'manager') && pending) {
        navigate('/onboardstaff');
        window.location.reload();
      } else if (access === 'staff') {
        navigate('/staffdash');
        window.location.reload();
      } else if (access === 'admin' || access === 'manager') {
        navigate('/dashboard');
        window.location.reload();
      }
    }
  }, [user, navigate, location]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        'https://api.insightos.co.uk/api/login',
        { ...form, captchaToken },
        { withCredentials: true }
      );

      if (data.success) {
        const { mfaRequired, id, trial, onboarded, companyCode } = data;
        const access = String(data.access || '').trim().toLowerCase();
        const staffOnboardPending = data.staffOnboardPending === true || data.staffOnboardPending === 1;

        if (mfaRequired) {
          navigate('/mfa', { state: { id } });
          window.location.reload();
          return;
        }
        if (trial === 2 && access === 'admin') {
          navigate('/paymenttrial', { state: { companyCode } });
          window.location.reload();
          return;
        }
        if (onboarded === false && access === 'admin') {
          navigate('/onboarding');
          window.location.reload();
          return;
        }
        if (access === 'sales') {
          navigate('/leads');
          window.location.reload();
          return;
        }
        if ((access === 'staff' || access === 'manager') && staffOnboardPending) {
          navigate('/onboardstaff');
          window.location.reload();
          return;
        }
        if (access === 'staff') {
          navigate('/staffdash');
          window.location.reload();
          return;
        }
        if (access === 'admin' || access === 'manager') {
          navigate('/dashboard');
          window.location.reload();
          return;
        }
      } else {
        setError('Login failed: Incorrect email, password, or company code.');
        setNeedsReset(true);
      }
    } catch {
      setError('An error occurred while logging in. Please try again.');
      setNeedsReset(true);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email) {
      setError('Please enter your email.');
      return;
    }

    if (!form.companyCode) {
      setError('Please enter your company code.');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        'https://api.insightos.co.uk/api/forgot-password',
        {
          email: form.email,
          companyCode: form.companyCode,
          captchaToken,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        navigate('/forgotpassword');
        window.location.reload();
        setError('Password reset link sent to your email.');
      } else {
        setError('Failed to send reset link. Please check your email and company code.');
        setNeedsReset(true);
      }
    } catch {
      setError('An error occurred. Please try again.');
      setNeedsReset(true);
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full bg-cover bg-no-repeat bg-center flex items-center justify-center px-4 py-8 overflow-hidden bg-slate-950"
         style={{ backgroundImage: "url('/skyline.png')" }}>

      {/* CSS-only floating orbs — runs on compositor thread, no JS cost */}
      

      {/* Orb 1 — indigo, top-left */}
      
      <InstallButton />

      <div className="relative z-10 w-full max-w-md flex flex-col">
        {/* Logo + heading */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mb-4 rounded-3xl border border-white/15 bg-white/10 px-5 py-4 shadow-2xl backdrop-blur-xl">
            <img src="1.png" alt="InsightOS" className="h-20 w-20 sm:h-24 sm:w-24 drop-shadow-xl" />
          </div>
          <div className="rounded-2xl border border-white/15 bg-slate-900/80 px-5 py-3 shadow-xl backdrop-blur-xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              {showForgot ? 'Reset Password' : 'Welcome back'}
            </h2>
          </div>
          <div className="rounded-2xl border border-white/15 bg-slate-900/80 px-5 py-3 shadow-xl backdrop-blur-xl mt-2">
          <p className="mt-1 text-sm text-slate-400">
            {showForgot
              ? 'Enter your details to receive a reset link.'
              : 'Sign in to your account to continue.'}
          </p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl px-6 py-7 sm:px-8 sm:py-8 shadow-2xl">
          <form className="space-y-5" onSubmit={showForgot ? handleForgotPassword : handleLogin}>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-4 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
              />
            </div>

            {/* Company Code */}
            <div>
              <label htmlFor="companyCode" className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
                Company Code
              </label>
              <input
                id="companyCode"
                name="companyCode"
                type="text"
                required
                value={form.companyCode}
                onChange={handleChange}
                placeholder="e.g. ACME123"
                className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-4 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
              />
            </div>

            {/* Password */}
            {!showForgot && (
              <div>
                <label htmlFor="password" className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 px-4 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>
            )}

            {/* Forgot password toggle */}
            <div className="flex justify-end -mt-2">
              <button
                type="button"
                className="text-xs text-indigo-400 hover:text-indigo-300 transition"
                onClick={() => { setShowForgot(!showForgot); setError(''); }}
              >
                {showForgot ? '← Back to Sign In' : 'Forgot password?'}
              </button>
            </div>

            {/* ReCAPTCHA */}
            <div className="flex justify-center">
              <div className="transform scale-90 origin-center">
                <ReCAPTCHA
                  sitekey="6LeEdM8rAAAAANDYuqmGgLkqw9MmSv_2TzTEai6Q"
                  onChange={setCaptchaToken}
                  theme="dark"
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2.5 text-center text-xs text-red-400">
                {error}
              </div>
            )}

            {/* Submit / Reset */}
            <div>
              {needsReset ? (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="w-full rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold py-3 transition-all shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  Reset
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white text-sm font-semibold py-3 transition-all shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                    loading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? 'Please wait…' : showForgot ? 'Send Reset Link' : 'Sign In'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginForm;
